import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import DateTimePicker from '~ComponentsImproved/DateTimePicker';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n, useLogger } from '~Hooks';
import { useStaffShifts } from '~Providers/StaffShiftsProvider';
import { GetShifts_operativeViewer_staffShifts } from '~Providers/StaffShiftsProvider/__queries__';
import { Box, Button, GreenButton } from '~Styles';
import PunchClockIcon from '~Assets/icons/MainMenu/punch-clock-outlined.svg';
import TimeManager from '~Components/TimeManager';

const ClockOutButton = styled(GreenButton)`
  white-space: nowrap;
  height: 40px;
`;

interface Props {
  shift: GetShifts_operativeViewer_staffShifts;
  onComplete: () => void;
  /** If provided, renders a Cancel button beside Clock out. */
  onCancel?: () => void;
  /** Override default header title. */
  title?: string;
  /** Override default header subtitle. Pass undefined to hide it. */
  subtitle?: string;
}

/**
 * Back-dated clock-out modal. Used both to force closure of a forgotten
 * previous-day shift and to let a manager clock out another staff at a
 * specific time. Picker is constrained to [clockedInAt, now].
 */
const CustomClockOut: FC<Props> = ({ shift, onComplete, onCancel, title, subtitle }) => {
  const { i18n } = useI18n();
  const logger = useLogger('custom-clock-out');
  const staffShifts = useStaffShifts();
  const isHandy = window.innerWidth <= 600;

  /** Shift always has clockedInAt at this point (handler only mounts us if so). */
  const clockedInDate = DateTime.fromISO(shift.clockedInAt ?? '');
  const now = DateTime.fromJSDate(TimeManager.now());

  /** Sensible default: clockedInAt + 4h, capped at now. */
  const fourHoursAfter = clockedInDate.plus({ hours: 4 });
  const initialPickerDate = fourHoursAfter < now ? fourHoursAfter : now;

  const [chosenISO, setChosenISO] = useState<string>(initialPickerDate.toISO() ?? '');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!chosenISO || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await staffShifts.clockOut(shift.staffId, chosenISO);
      logger.info('Backdated clock-out for previous-day shift', {
        staffId: shift.staffId,
        shiftId: shift.id,
        chosenISO,
      });
      await staffShifts.refetch();
      onComplete();
    } catch (err) {
      logger.error('Backdated clock-out failed', {
        staffId: shift.staffId,
        shiftId: shift.id,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
      setSubmitting(false);
    }
  };

  const submitButton = useButton({
    onClick: onSubmit,
    disabled: submitting || !chosenISO,
  });

  const cancelButton = useButton({
    onClick: () => onCancel?.(),
    disabled: submitting,
  });

  /** When defaults aren't overridden, show the previous-day forgotten-shift copy. */
  const headerTitle = title ?? i18n('StaffShifts.PreviousShiftNotClockedOut');
  const headerSubtitle =
    subtitle !== undefined ? subtitle : i18n('StaffShifts.PreviousShiftPrompt');

  return (
    <Modal isShowing={true} backdropClose={false}>
      <ModalWrapper
        isHandy={isHandy}
        header={{
          icon: PunchClockIcon,
          title: headerTitle,
          subtitle: headerSubtitle,
        }}>
        <Box fluid display="flex" flexDirection="column" alignItems="center">
          <Paragraph level={4} color="var(--colorBlueGray1200)">
            {i18n('StaffShifts.PreviousClockIn')}:{' '}
            <strong>{clockedInDate.toFormat('d LLL HH:mm')}</strong>
          </Paragraph>
          <DateTimePicker
            dark
            initialValue={initialPickerDate.toJSDate()}
            min={clockedInDate.toJSDate()}
            max={now.toJSDate()}
            onChange={(val) => val && setChosenISO(val)}
          />
          <Box fluid display="flex" gap="small" marginTop="medium">
            {onCancel && (
              <Button fluid {...cancelButton}>
                {i18n('General.Cancel')}
              </Button>
            )}
            <ClockOutButton fluid {...submitButton}>
              {submitting ? (
                <Loader size="sm" color="var(--colorWhite)" />
              ) : (
                i18n('StaffShifts.ClockOut')
              )}
            </ClockOutButton>
          </Box>
        </Box>
      </ModalWrapper>
    </Modal>
  );
};

export default CustomClockOut;
